// src/pages/driverReports/RideHailingReport.js
import React, { useEffect, useState } from "react";
import { db } from "../../firebase/firebase";
import { collection, getDocs, } from "firebase/firestore";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";

const WEEKS = ["week1", "week2", "week3", "week4", "week5"];

const WEEK_COLORS = {
  week1: "#e3f2fd", // light blue
  week2: "#e8f5e9", // light green
  week3: "#fffde7", // light yellow
  week4: "#fff3e0", // light orange
  week5: "#f3e5f5", // light purple
};

export default function RideHailingReport({ platform }) {
  const platformLower = platform.toLowerCase();
  console.log("🚨 RideHailingReport RENDERING with platform:", platform, "->", platformLower);
  const [month, setMonth] = useState("");
  const [weekFrom, setWeekFrom] = useState("week1");
  const [weekTo, setWeekTo] = useState("week5");
  const [companyFilter, setCompanyFilter] = useState(""); // Optional: filter by company

  const [loading, setLoading] = useState(false);

  const [rows, setRows] = useState([]);
  const [columns, setColumns] = useState([]); // [{key,label,weekKey,type}]
  const [weekTotals, setWeekTotals] = useState({}); // {week1:{bank,cash},...}
  const [grandTotals, setGrandTotals] = useState({
    bank: 0,
    cash: 0,
    total: 0,
  });

  const [driversMap, setDriversMap] = useState({}); // firestoreId -> { fullName, company }

  // Load driver master names once
  useEffect(() => {
    async function loadDrivers() {
      const snap = await getDocs(collection(db, "drivers"));
      const map = {};
      snap.forEach((d) => {
        const data = d.data();
        map[d.id] = { fullName: data.fullName || data.name || "Unknown", company: data.company || "" };
      });
      setDriversMap(map);
    }
    loadDrivers();
  }, []);

  // Clear report when platform changes
  useEffect(() => {
    setRows([]);
    setColumns([]);
    setWeekTotals({});
    setGrandTotals({ bank: 0, cash: 0, total: 0 });
  }, [platform]);

  // Debug: Log when component loads and state changes
  useEffect(() => {
    console.log("🔧 RideHailingReport Component Loaded");
    console.log("🔧 Props:", { platform });
    console.log("🔧 State:", { month, weekFrom, weekTo });
    console.log("🔧 Drivers loaded:", Object.keys(driversMap).length, "drivers");
  }, [platform, month, weekFrom, weekTo, driversMap]);

  async function generateReport() {
  if (!month) {
    alert("Please select a month.");
    return;
  }

  const fromIndex = WEEKS.indexOf(weekFrom);
  const toIndex = WEEKS.indexOf(weekTo);
  if (fromIndex === -1 || toIndex === -1 || fromIndex > toIndex) {
    alert("Invalid week range.");
    return;
  }

  const selectedWeeks = WEEKS.slice(fromIndex, toIndex + 1);

  setLoading(true);
  try {
    console.log("🚀 Starting report generation...");
    console.log("📊 Parameters:", { platform, month, weekFrom, weekTo, selectedWeeks });

    // Get ALL driver documents first
    const driversCollection = collection(db, "driverWeeklyEarnings");
    const driversSnap = await getDocs(driversCollection);
    
    console.log(`📋 Found ${driversSnap.size} drivers in driverWeeklyEarnings`);
    
    const allEarnings = [];
    let driverCount = 0;
    
    // For each driver, get their uploads subcollection
    for (const driverDoc of driversSnap.docs) {
      driverCount++;
      const driverId = driverDoc.id;
      const uploadsRef = collection(db, "driverWeeklyEarnings", driverId, "uploads");
      const uploadsSnap = await getDocs(uploadsRef);
      
      console.log(`   Driver ${driverCount}/${driversSnap.size}: ${driverId} has ${uploadsSnap.size} uploads`);
      
      uploadsSnap.forEach((uploadDoc) => {
        const data = uploadDoc.data();
        const weekKey = (data.week || "").toLowerCase();
        const dataPlatform = data.platform?.toLowerCase();
        const dataMonth = data.month;
        
        // Debug each document
        const matches = dataPlatform === platform.toLowerCase() && 
                       dataMonth === month && 
                       selectedWeeks.includes(weekKey);
        
        if (matches) {
          console.log(`   ✅ Match found: ${driverId} | ${dataPlatform} | ${dataMonth} | ${weekKey} | Bank: ${data.bankIncome} | Cash: ${data.cashIncome}`);
          allEarnings.push({
            ...data,
            driverId: driverId,
          });
        }
      });
    }

    console.log(`📈 Total matching records found: ${allEarnings.length} for ${platform} in ${month}`);
    
    if (allEarnings.length === 0) {
      console.log("❌ No matching earnings found. Check:");
      console.log("   - Platform spelling (case-insensitive)");
      console.log("   - Month format (YYYY-MM)");
      console.log("   - Data exists in Firestore");
      console.log("   - Firestore path: driverWeeklyEarnings/{driverId}/uploads/{docId}");
    }

    // Group by platform driver id (sourceDriverId)
    const drivers = {}; // platformId -> aggregated row

    allEarnings.forEach((d, index) => {
      const weekKey = (d.week || "").toLowerCase();
      if (!selectedWeeks.includes(weekKey)) return;

      const platformId = d.sourceDriverId || d.driverId || "UNKNOWN";
      const firestoreDriverId = d.driverId;

      // derive driver meta (name/company) safely for logs + row building
      const meta = (firestoreDriverId && driversMap[firestoreDriverId]) || null;
      const driverName = (meta && meta.fullName) || d.driverName || "Unknown Driver";
      const company = (meta && meta.company) || "";

      console.log(`   Processing record ${index + 1}/${allEarnings.length}:`, {
        platformId,
        firestoreDriverId,
        driverName,
        company,
        week: weekKey,
        bank: d.bankIncome,
        cash: d.cashIncome
      });

      if (!drivers[platformId]) {drivers[platformId] = {
          platformDriverId: platformId,
          driverName,
          company,
          weeks: {}, // weekKey -> {bank,cash}
          totalBank: 0,
          totalCash: 0,
          total: 0,
        };
        
        console.log(`   Created new driver entry: ${platformId} -> ${driverName}`);
      }

      const bank = Number(d.bankIncome || 0);
      const cash = Number(d.cashIncome || 0);
      const total = bank + cash;

      drivers[platformId].weeks[weekKey] = {
        bank: (drivers[platformId].weeks[weekKey]?.bank || 0) + bank,
        cash: (drivers[platformId].weeks[weekKey]?.cash || 0) + cash,
      };
      drivers[platformId].totalBank += bank;
      drivers[platformId].totalCash += cash;
      drivers[platformId].total += total;
      
      console.log(`   Added to ${platformId}: Week ${weekKey} | Bank +${bank} | Cash +${cash}`);
    });

    console.log(`👥 Final drivers object:`, Object.keys(drivers).length, "drivers found");
    console.log("📊 Drivers data:", drivers);

    // Build dynamic week columns (only BANK + CASH, no "total" per week)
    const cols = selectedWeeks.flatMap((w) => [
      {
        key: `${w}_bank`,
        label: `${w.toUpperCase()} BANK`,
        weekKey: w,
        type: "bank",
      },
      {
        key: `${w}_cash`,
        label: `${w.toUpperCase()} CASH`,
        weekKey: w,
        type: "cash",
      },
    ]);
    setColumns(cols);

    // Build final row array for UI
    const finalRows = Object.values(drivers).map((d) => {
      const row = {
        platformDriverId: d.platformDriverId,
        driverName: d.driverName,
        company: d.company || "",
        totalBank: d.totalBank,
        totalCash: d.totalCash,
        total: d.total,
      };

      selectedWeeks.forEach((w) => {
        const wData = d.weeks[w] || { bank: 0, cash: 0 };
        row[`${w}_bank`] = wData.bank;
        row[`${w}_cash`] = wData.cash;
      });

      return row;
    });

    console.log(`📋 Final rows for UI:`, finalRows.length, "rows");
    console.log("📋 Sample row:", finalRows[0]);

    setRows(finalRows);

    // Compute column totals per week
    const weekTotalsObj = {};
    selectedWeeks.forEach((w) => {
      let bankSum = 0;
      let cashSum = 0;
      finalRows.forEach((r) => {
        bankSum += r[`${w}_bank`] || 0;
        cashSum += r[`${w}_cash`] || 0;
      });
      weekTotalsObj[w] = { bank: bankSum, cash: cashSum };
    });
    setWeekTotals(weekTotalsObj);

    // Grand totals
    let gBank = 0;
    let gCash = 0;
    let gTotal = 0;
    finalRows.forEach((r) => {
      gBank += r.totalBank || 0;
      gCash += r.totalCash || 0;
      gTotal += r.total || 0;
    });
    setGrandTotals({ bank: gBank, cash: gCash, total: gTotal });
    
    console.log("✅ Report generation completed!");
    console.log("📊 Summary:", {
      rows: finalRows.length,
      weeks: selectedWeeks,
      grandTotals: { bank: gBank, cash: gCash, total: gTotal }
    });
  } catch (error) {
    console.error("❌ Error generating report:", error);
    alert(`Error generating report: ${error.message}`);
  } finally {
    setLoading(false);
  }
}

  async function downloadExcel() {
    if (!displayedRows.length) {
      alert("No data to export. Please generate report first.");
      return;
    }

    const wb = new ExcelJS.Workbook();
    const ws = wb.addWorksheet("Report");

    const header = [
      "#",
      "Platform Driver ID",
      "Driver Name",
      "Company",
      ...columns.map((c) => c.label),
      "Total Bank",
      "Total Cash",
      "Grand Total",
    ];

    ws.addRow(header);

    // Style header
    const headerRow = ws.getRow(1);
    headerRow.font = { bold: true };
    headerRow.alignment = { vertical: "middle", horizontal: "center" };

    headerRow.eachCell((cell, colNumber) => {
      cell.border = {
        top: { style: "thin" },
        left: { style: "thin" },
        bottom: { style: "thin" },
        right: { style: "thin" },
      };
      // Week color fill
      if (colNumber > 4 && colNumber <= 4 + columns.length) {
        const colIndex = colNumber - 5;
        const colMeta = columns[colIndex];
        const fillColor = WEEK_COLORS[colMeta.weekKey] || "#ffffff";
        cell.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: rgbToHex(fillColor) },
        };
      } else {
        cell.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: "FFEFEFEF" },
        };
      }
    });

    // Data rows
    displayedRows.forEach((r, idx) => {
      const rowData = [
        idx + 1,
        r.platformDriverId,
        r.driverName,
        (r.company || ""),
        ...columns.map((c) => Number(r[c.key] || 0)),
        r.totalBank,
        r.totalCash,
        r.total,
      ];
      const excelRow = ws.addRow(rowData);

      excelRow.eachCell((cell, colNumber) => {
        cell.border = {
          top: { style: "thin" },
          left: { style: "thin" },
          bottom: { style: "thin" },
          right: { style: "thin" },
        };
        if (colNumber > 4) {
          cell.numFmt = "0.00";
        }
      });
    });

    // Totals row
    const totalRowValues = [
      "TOTAL",
      "",
      "",
      "",
      ...columns.map((c) => {
        const w = c.weekKey;
        const type = c.type; // "bank" | "cash"
        const val = computedWeekTotals[w]?.[type] || 0;
        return val;
      }),
      grandTotals.bank,
      grandTotals.cash,
      grandTotals.total,
    ];

    const totalRow = ws.addRow(totalRowValues);
    totalRow.font = { bold: true };

    totalRow.eachCell((cell, colNumber) => {
      cell.border = {
        top: { style: "thin" },
        left: { style: "thin" },
        bottom: { style: "thin" },
        right: { style: "thin" },
      };
      if (colNumber > 4) {
        cell.numFmt = "0.00";
      }
      cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "FFE0E0E0" },
      };
    });

    // Auto width
    ws.columns.forEach((col) => {
      let maxLen = 10;
      col.eachCell({ includeEmpty: true }, (cell) => {
        const v = cell.value ? cell.value.toString() : "";
        if (v.length > maxLen) maxLen = v.length;
      });
      col.width = maxLen + 2;
    });

    const fileName = `${platform.toLowerCase()}_${month}_${weekFrom}-${weekTo}.xlsx`;
    const buf = await wb.xlsx.writeBuffer();
    saveAs(new Blob([buf]), fileName);
  }

  // Helper: convert CSS hex (#rrggbb) to Excel ARGB
  function rgbToHex(cssHex) {
    // cssHex like "#e3f2fd"
    const hex = cssHex.replace("#", "");
    return "FF" + hex.toUpperCase();
  }

    const companies = Array.from(
    new Set((rows || []).map((r) => r.company).filter(Boolean))
  ).sort();

  const displayedRows = companyFilter
    ? (rows || []).filter((r) => r.company === companyFilter)
    : rows || [];

  // Totals should respect company filter
  const computedWeekTotals = {};
  columns.forEach((c) => {
    const w = c.weekKey;
    const type = c.type; // "bank" | "cash"
    if (!computedWeekTotals[w]) computedWeekTotals[w] = { bank: 0, cash: 0 };
    const sum = displayedRows.reduce((acc, r) => acc + Number(r[c.key] || 0), 0);
    computedWeekTotals[w][type] = sum;
  });

  const computedGrandTotals = displayedRows.reduce(
    (acc, r) => {
      acc.bank += Number(r.totalBank || 0);
      acc.cash += Number(r.totalCash || 0);
      acc.total += Number(r.total || 0);
      return acc;
    },
    { bank: 0, cash: 0, total: 0 }
  );

return (
    <div style={{ marginTop: 20 }}>
      <h2>{platform} — Driver Weekly Income Report</h2>
      {console.log("🔄 RENDERING UI with platform:", platform, "month:", month)}

      {/* Filters */}
      <div
        style={{
          display: "flex",
          gap: 20,
          marginTop: 10,
          alignItems: "flex-end",
          flexWrap: "wrap",
        }}
      >
        <div>
          <label>Month</label>
          <br />
          <input
            type="month"
            value={month}
            onChange={(e) => setMonth(e.target.value)}
          />
        </div>

        <div>
          <label>Week From</label>
          <br />
          <select
            value={weekFrom}
            onChange={(e) => setWeekFrom(e.target.value)}
          >
            {WEEKS.map((w) => (
              <option key={w} value={w}>
                {w.toUpperCase()}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label>Week To</label>
          <br />
          <select value={weekTo} onChange={(e) => setWeekTo(e.target.value)}>
            {WEEKS.map((w) => (
              <option key={w} value={w}>
                {w.toUpperCase()}
              </option>
            ))}
          </select>
        </div>

                <div>
          <label>Company (Optional)</label>
          <br />
          <select
            value={companyFilter}
            onChange={(e) => setCompanyFilter(e.target.value)}
            style={{ minWidth: 200 }}
          >
            <option value="">All Companies</option>
            {companies.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
        </div>

        <button
          onClick={generateReport}
          disabled={loading}
          style={{
            padding: "10px 16px",
            background: "#0037ff",
            color: "white",
            borderRadius: 6,
            border: "none",
            cursor: "pointer",
            minWidth: 120,
          }}
        >
          {loading ? "Loading..." : "Generate"}
        </button>

        {displayedRows.length > 0 && (
          <button
            onClick={downloadExcel}
            style={{
              padding: "10px 16px",
              background: "green",
              color: "white",
              borderRadius: 6,
              border: "none",
              cursor: "pointer",
            }}
          >
            Download Excel
          </button>
        )}
      </div>

      {loading && (
        <p style={{ marginTop: 15 }}>⏳ Generating report, please wait…</p>
      )}

      {/* TABLE */}
      {displayedRows.length > 0 && (
        <table
          style={{
            marginTop: 20,
            width: "100%",
            borderCollapse: "collapse",
            fontSize: 13,
            border: "1px solid #ccc",
          }}
        >
          <thead>
            <tr style={{ background: "#f3f4ff" }}>
              <th style={{ border: "1px solid #ccc", padding: 6 }}>#</th>
              <th style={{ border: "1px solid #ccc", padding: 6 }}>
                Platform Driver ID
              </th>
              <th style={{ border: "1px solid #ccc", padding: 6 }}>
                Driver Name
              </th>
              <th style={{ border: "1px solid #ccc", padding: 6 }}>
                Company
              </th>

              {columns.map((c, idx) => (
                <th
                  key={c.key}
                  style={{
                    border: "1px solid #ccc",
                    padding: 6,
                    background: WEEK_COLORS[c.weekKey] || "#ffffff",
                  }}
                >
                  {c.label}
                </th>
              ))}

              <th style={{ border: "1px solid #ccc", padding: 6 }}>
                Total Bank
              </th>
              <th style={{ border: "1px solid #ccc", padding: 6 }}>
                Total Cash
              </th>
              <th style={{ border: "1px solid #ccc", padding: 6 }}>
                Grand Total
              </th>
            </tr>
          </thead>

          <tbody>
            {displayedRows.map((r, idx) => (
              <tr key={r.platformDriverId}>
                <td style={{ border: "1px solid #ccc", padding: 6 }}>
                  {idx + 1}
                </td>
                <td style={{ border: "1px solid #ccc", padding: 6 }}>{r.platformDriverId}</td>

                <td style={{ border: "1px solid #ccc", padding: 6 }}>{r.driverName}</td>

                <td style={{ border: "1px solid #ccc", padding: 6 }}>{r.company || ""}</td>

                {columns.map((c) => (
                  <td
                    key={c.key}
                    style={{
                      border: "1px solid #ccc",
                      padding: 6,
                      textAlign: "right",
                      background: WEEK_COLORS[c.weekKey] || "#ffffff",
                    }}
                  >
                    {Number(r[c.key] || 0).toFixed(2)}
                  </td>
                ))}

                <td
                  style={{
                    border: "1px solid #ccc",
                    padding: 6,
                    textAlign: "right",
                  }}
                >
                  {r.totalBank.toFixed(2)}
                </td>
                <td
                  style={{
                    border: "1px solid #ccc",
                    padding: 6,
                    textAlign: "right",
                  }}
                >
                  {r.totalCash.toFixed(2)}
                </td>
                <td
                  style={{
                    border: "1px solid #ccc",
                    padding: 6,
                    textAlign: "right",
                  }}
                >
                  {r.total.toFixed(2)}
                </td>
              </tr>
            ))}

            {/* TOTAL ROW */}
            <tr
              style={{
                fontWeight: "bold",
                background: "#eef2ff",
              }}
            >
              <td
                style={{
                  border: "1px solid #ccc",
                  padding: 6,
                  textAlign: "center",
                }}
              >
                TOTAL
              </td>
              <td
                style={{
                  border: "1px solid #ccc",
                  padding: 6,
                }}
              ></td>
              <td style={{ border: "1px solid #ccc", padding: 6 }}></td>
              <td style={{ border: "1px solid #ccc", padding: 6 }}></td>

              
              {columns.map((c) => {
                const w = c.weekKey;
                const type = c.type; // "bank" | "cash"
                const val = computedWeekTotals[w]?.[type] || 0;
                return (
                  <td
                    key={c.key}
                    style={{
                      border: "1px solid #ccc",
                      padding: 6,
                      textAlign: "right",
                      background: WEEK_COLORS[w] || "#ffffff",
                    }}
                  >
                    {val.toFixed(2)}
                  </td>
                );
              })}

              <td
                style={{
                  border: "1px solid #ccc",
                  padding: 6,
                  textAlign: "right",
                }}
              >
                {computedGrandTotals.bank.toFixed(2)}
              </td>
              <td
                style={{
                  border: "1px solid #ccc",
                  padding: 6,
                  textAlign: "right",
                }}
              >
                {computedGrandTotals.cash.toFixed(2)}
              </td>
              <td
                style={{
                  border: "1px solid #ccc",
                  padding: 6,
                  textAlign: "right",
                }}
              >
                {computedGrandTotals.total.toFixed(2)}
              </td>
            </tr>
          </tbody>
        </table>
      )}
    </div>
  );
}